'use client';

import React, { useRef, useEffect } from 'react';
import BgMedia from '../backgrounds/bg_media';
import ButtonLink from '../../buttons/button_link';

// This component is used outside of Storyblok context (hence the file name in camelCase), and is noteditable in Storyblok.

/**
 * Renders a teaser card component.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.blok - The data for the teaser card.
 * @param {boolean} [props.inSlider=false] - Indicates if the card is in a slider.
 * @param {Function} [props.returnCardWidth=null] - Callback function to return the card width to the parent component.
 * @returns {JSX.Element} The rendered teaser card component.
 */

const TeaserCard = ({ blok, inSlider = false, returnCardWidth = null }) => {
  const cardRef = useRef(null);

  const mediaParams = {
    backgroundMedia: {
      filename:
        blok.image.filename ||
        'https://a.storyblok.com/f/260037/24x24/8d8832507a/question_black.svg',
      alt: blok.image.alt,
    },
    inSlider: inSlider,
  };

  /**
   * useEffect hook to handle resizing and return the card width.
   */

  useEffect(() => {
    if (returnCardWidth && cardRef.current) {
      const handleResize = () => {
        returnCardWidth(cardRef.current.offsetWidth);
      };
      handleResize();

      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
    }
  }, [returnCardWidth]);

  return (
    <section
      ref={returnCardWidth ? cardRef : null}
      className={`flex flex-col col-span-12 md:col-span-6 lg:col-span-5 xl:col-span-4 mt-4 ${
        inSlider ? 'snap-always snap-start' : ''
      }`}
    >
      <div className="mb-4 px-0 w-full md:max-w-[60vw] h-[30vh] min-h-32 max-h-80 relative">
        <BgMedia mediaParams={mediaParams} />
      </div>
      <div className="flex flex-col px-4">
        <h1 className="text-base mt-4">{blok.title}</h1>
        <p className="text-sm">{blok.text}</p>
      </div>
      <div className="px-4 mt-auto">
        <ButtonLink blok={blok.button} />
      </div>
    </section>
  );
};

export default TeaserCard;
